import { z } from 'zod'

import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  DeliverableDetailsSchema,
  DeliverableDomainSelectModelSchema,
} from './deliverable.models.ts'
import {
  DescriptionSchema,
  DollarsSchema,
  NameSchema,
  TranslationsSchema,
} from './property.models.ts'
import { TagFilterSchema, TagIdsByCategoryMapSchema } from './tag.models.ts'
import { ServiceIdSchema } from './unique-id.models.ts'

/**
 * ServiceStatus
 */
export const [ServiceStatuses, ServiceStatusSchema, ServiceStatusEnum] =
  EnumBuilder('draft', 'published', 'archived')
export type ServiceStatus = ZodInfer<typeof ServiceStatusSchema>

/**
 * ServiceType
 */
export const [ServiceTypes, ServiceTypeSchema, ServiceTypeEnum] = EnumBuilder(
  'included',
  'standard',
  'custom'
)
export type ServiceType = ZodInfer<typeof ServiceTypeSchema>

/**
 * Service Category
 */
export const [ServicesCategories, ServiceCategorySchema] = EnumBuilder(
  'included',
  'add-on'
)
export type ServiceCategory = ZodInfer<typeof ServiceCategorySchema>

/**
 * ServiceType to ServiceCategory map
 */
export const ServiceTypeCategoryMap: Record<ServiceType, ServiceCategory> = {
  custom: 'add-on',
  included: 'included',
  standard: 'add-on',
}

/**
 * ServiceKey
 */ export const [ServiceKeys, ServiceKeySchema, ServiceKeyEnum] = EnumBuilder(
  'pre_production_review_round',
  'post_production_review_round',
  'legacy_quickframe_service',
  'change_order_service'
)
export type ServiceKey = ZodInfer<typeof ServiceKeySchema>

/**
 * PreProductionReview
 */
export const PreProductionReviewSchema = z.boolean()
export type PreProductionReview = ZodInfer<typeof PreProductionReviewSchema>

/**
 * ReferenceFileRequired
 */
export const ReferenceFileRequiredSchema = z.boolean()
export type ReferenceFileRequired = ZodInfer<typeof ReferenceFileRequiredSchema>

/**
 * Max
 */
export const MaxSchema = z.int().positive()
export type Max = ZodInfer<typeof MaxSchema>

/**
 * ServiceTranslations
 */
export const ServiceTranslationsSchema = TranslationsSchema(
  'name',
  'description'
)
export type ServiceTranslations = ZodInfer<typeof ServiceTranslationsSchema>

/**
 * ServiceDomainSelectModel
 */
export const ServiceDomainSelectModelSchema = z.object({
  serviceId: ServiceIdSchema,
  name: NameSchema,
  description: DescriptionSchema,
  serviceKey: ServiceKeySchema.optional(),
  translations: ServiceTranslationsSchema.optional(),
  deliverables: z.array(DeliverableDetailsSchema).optional(),
  preProductionReview: PreProductionReviewSchema,
  preProductionBrandReferenceFileRequired: ReferenceFileRequiredSchema,
  preProductionMakerReferenceFileRequired: ReferenceFileRequiredSchema,
  max: MaxSchema.optional(),
  status: ServiceStatusSchema,
})

export type ServiceDomainSelectModel = ZodInfer<
  typeof ServiceDomainSelectModelSchema
>

/**
 * ServiceDomainInsertModel
 */
export const ServiceDomainInsertModelSchema =
  ServiceDomainSelectModelSchema.omit({
    serviceKey: true, // Internal use only
    status: true, // Always inserted as draft
  })
    .extend({
      // Defaults only apply at insert time — base schemas are default-free
      // so that .partial() works correctly in update models.
      preProductionReview: PreProductionReviewSchema.prefault(false),
      preProductionBrandReferenceFileRequired:
        ReferenceFileRequiredSchema.prefault(false),
      preProductionMakerReferenceFileRequired:
        ReferenceFileRequiredSchema.prefault(false),
    })
    .partial({ serviceId: true })

export type ServiceDomainInsertModel = ZodInfer<
  typeof ServiceDomainInsertModelSchema
>

/**
 * ServiceDomainUpdateModel
 */
export const ServiceDomainUpdateModelSchema =
  ServiceDomainSelectModelSchema.extend({
    max: ServiceDomainSelectModelSchema.shape.max.nullable(),
  })
    .pick({
      name: true,
      description: true,
      translations: true,
      deliverables: true,
      preProductionReview: true,
      preProductionBrandReferenceFileRequired: true,
      preProductionMakerReferenceFileRequired: true,
      max: true,
      status: true,
    })
    .extend({ tags: TagIdsByCategoryMapSchema })
    .partial()

export type ServiceDomainUpdateModel = ZodInfer<
  typeof ServiceDomainUpdateModelSchema
>

/**
 * ServiceDomainSelectModelFilter
 */
export const ServiceDomainSelectModelFiltersSchema = z
  .object({
    serviceId: ModelFilterSchema(ServiceIdSchema),
    serviceKey: ModelFilterSchema(ServiceKeySchema),
    status: ModelFilterSchema(ServiceStatusSchema),
    tags: TagFilterSchema,
  })
  .partial()

export type ServiceDomainSelectModelFilters = ZodInfer<
  typeof ServiceDomainSelectModelFiltersSchema
>

/**
 * ServicePayoutSchema
 */
export const ServicePayoutMax = 500_000
export const ServicePayoutSchema = DollarsSchema(ServicePayoutMax)
export type ServicePayout = ZodInfer<typeof ServicePayoutSchema>

const ServiceDeliverableSchema = z.union([
  DeliverableDetailsSchema,
  DeliverableDomainSelectModelSchema.pick({ details: true }),
])

export type ServiceDeliverable = ZodInfer<typeof ServiceDeliverableSchema>

const ServiceLikeSchema = ServiceDomainSelectModelSchema.pick({
  serviceId: true,
  serviceKey: true,
}).extend({
  deliverables: z.array(ServiceDeliverableSchema).optional(),
  serviceType: ServiceTypeSchema.optional(),
})

export type ServiceLike = ZodInfer<typeof ServiceLikeSchema>

/**
 * Type guard for services that have deliverables
 * @param service the service to be checked
 * @returns true if the service has deliverables
 */
export type ServiceWithDeliverables<Service extends ServiceLike> = Service & {
  deliverables: Array<ServiceDeliverable>
}

/**
 * Helper function to unwrap the details property of a deliverable
 * @param deliverable a deliverable details object or a deliverable details object wrapped in a details property
 * @returns the deliverable details object
 */
export const getServiceDeliverableDetails = (
  deliverable: ServiceDeliverable
) => ('details' in deliverable ? deliverable.details : deliverable)

export const serviceWithDeliverables = <Service extends ServiceLike>(
  service: Service
): service is ServiceWithDeliverables<Service> =>
  z.array(ServiceDeliverableSchema).nonempty().safeParse(service.deliverables)
    .success

/**
 * Extracts services from an array that have deliverables
 * @param services the array of services
 * @returns an array of services that have deliverables
 */
export const servicesWithDeliverables = <Service extends ServiceLike>(
  services: Array<Service>
) => services.filter(serviceWithDeliverables)

/**
 * Type guard for services that do not have deliverables
 * @param service the service to be checked
 * @returns true if the service does not have deliverables
 */
export type ServiceWithoutDeliverable<Service extends ServiceLike> = Service & {
  deliverables: undefined
}

export const serviceWithoutDeliverables = <Service extends ServiceLike>(
  service: Service
): service is ServiceWithoutDeliverable<Service> =>
  !serviceWithDeliverables(service)

/**
 * Excludes services from an array that have deliverables
 * @param services an array of models
 * @returns an array of models that do not have deliverables
 */
export const servicesWithoutDeliverables = <Service extends ServiceLike>(
  services: Array<Service>
) => services.filter(serviceWithoutDeliverables)

export const servicesByStatus =
  (statuses: ServiceStatus[]) =>
  ({ status }: ServiceDomainSelectModel) =>
    statuses.includes(status)

type CustomService<Service extends ServiceLike> = Service & {
  serviceType: 'custom'
}

type StandardService<Service extends ServiceLike> = Service & {
  serviceType: 'standard'
}

type IncludedService<Service extends ServiceLike> = Service & {
  serviceType: 'included'
}

export const isCustomService = <Service extends ServiceLike>(
  service: Service
): service is CustomService<Service> => service.serviceType === 'custom'

export const isStandardService = <Service extends ServiceLike>(
  service: Service
): service is StandardService<Service> => service.serviceType === 'standard'

export const isIncludedService = <Service extends ServiceLike>(
  service: Service
): service is IncludedService<Service> => service.serviceType === 'included'
